import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import { FaCalendarAlt, FaSearch, FaFilter, FaBroom } from 'react-icons/fa';
import { FaFileExcel } from 'react-icons/fa6';
import { format, parseISO } from 'date-fns';
import Select from 'react-select';
import * as XLSX from 'xlsx';
import { getDefaultDates } from '../utils/dateUtils';
import '../styles/ResumePointagePage.css';

const ResumePointagePage = () => {
  // =========================
  // ✅ States
  // =========================
  const { startDate: defaultStart, endDate: defaultEnd } = getDefaultDates();
  const [start, setStart] = useState(defaultStart);
  const [end, setEnd] = useState(defaultEnd);

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // 🎛️ Filtres (multi)
  const [chantierFilters, setChantierFilters] = useState([]); // array of strings
  const [ouvrierFilters, setOuvrierFilters] = useState([]); // array of strings

  // ⚙️ Options affichage
  const [hideZeros, setHideZeros] = useState(true);
  const [showChantierTotals, setShowChantierTotals] = useState(true);

  // =========================
  // 🔎 Helpers
  // =========================
  const normalizeText = (s = '') =>
    String(s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();

  const isValidRange = useMemo(() => {
    if (!start || !end) return false;
    return start <= end;
  }, [start, end]);

  // =========================
  // 📡 API
  // =========================
  const fetchData = async () => {
    setError('');

    if (!start || !end) {
      setError('📅 Veuillez choisir une date de début et de fin.');
      return;
    }
    if (start > end) {
      setError('⚠️ La date de début doit être inférieure ou égale à la date de fin.');
      return;
    }

    try {
      setLoading(true);
      const res = await axios.get(`/pointages?start=${start}&end=${end}`);
      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError("❌ Erreur lors du chargement des pointages.");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // auto-load au montage
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // =========================
  // 🧠 Pivot (useMemo) - dates ISO key
  // pivot[chantier][ouvrier][dayKey] = {jours, heures}
  // =========================
  const { pivot, dateKeys, dateLabelsByKey, chantiersList, ouvriersList } = useMemo(() => {
    const pivotData = {};
    const uniqueDayKeys = new Set();
    const labelsByKey = {};

    (data || []).forEach((p) => {
      const chantier = p?.chantier || '—';
      const ouvrierName = p?.ouvrier?.name || '—';
      const ouvrierCin = p?.ouvrier?.cin || '—';
      const ouvrier = `${ouvrierName} (${ouvrierCin})`;

      // 🔑 clé stable ISO
      const dayKey = format(parseISO(p.date), 'yyyy-MM-dd');
      // 🏷️ label affichage
      const dayLabel = format(parseISO(p.date), 'dd/MM/yyyy');

      uniqueDayKeys.add(dayKey);
      labelsByKey[dayKey] = dayLabel;

      if (!pivotData[chantier]) pivotData[chantier] = {};
      if (!pivotData[chantier][ouvrier]) pivotData[chantier][ouvrier] = {};

      pivotData[chantier][ouvrier][dayKey] = {
        jours: Number(p.jours || 0),
        heures: Number(p.heures || 0),
      };
    });

    const sortedKeys = Array.from(uniqueDayKeys).sort((a, b) => a.localeCompare(b));

    const chantiers = Object.keys(pivotData).sort((a, b) => a.localeCompare(b));
    const ouvriers = Array.from(
      new Set(Object.values(pivotData).flatMap((obj) => Object.keys(obj)))
    ).sort((a, b) => a.localeCompare(b));

    return {
      pivot: pivotData,
      dateKeys: sortedKeys,
      dateLabelsByKey: labelsByKey,
      chantiersList: chantiers,
      ouvriersList: ouvriers,
    };
  }, [data]);

  // =========================
  // 🎯 react-select options + “Select All”
  // =========================
  const chantierOptions = useMemo(() => {
    const base = chantiersList.map((ch) => ({ label: ch, value: ch }));
    return [{ label: '✅ Tous les chantiers', value: '__ALL__' }, ...base];
  }, [chantiersList]);

  const ouvrierOptions = useMemo(() => {
    const base = ouvriersList.map((o) => ({ label: o, value: o }));
    return [{ label: '✅ Tous les ouvriers', value: '__ALL__' }, ...base];
  }, [ouvriersList]);

  const chantierValue = useMemo(() => {
    return chantierFilters.map((v) => ({ label: v, value: v }));
  }, [chantierFilters]);

  const ouvrierValue = useMemo(() => {
    return ouvrierFilters.map((v) => ({ label: v, value: v }));
  }, [ouvrierFilters]);

  const filterOption = (candidate, input) => {
    if (!input) return true;
    return normalizeText(candidate.label).includes(normalizeText(input));
  };

  const handleChantierChange = (selected) => {
    const arr = selected || [];
    const hasAll = arr.some((x) => x.value === '__ALL__');
    if (hasAll) {
      setChantierFilters([...chantiersList]);
      return;
    }
    setChantierFilters(arr.map((x) => x.value));
  };

  const handleOuvrierChange = (selected) => {
    const arr = selected || [];
    const hasAll = arr.some((x) => x.value === '__ALL__');
    if (hasAll) {
      setOuvrierFilters([...ouvriersList]);
      return;
    }
    setOuvrierFilters(arr.map((x) => x.value));
  };

  const clearFilters = () => {
    setChantierFilters([]);
    setOuvrierFilters([]);
  };

  // =========================
  // 🧮 Sums
  // =========================
  const sum = (vals, key) => vals.reduce((acc, val) => acc + (Number(val?.[key]) || 0), 0);

  // Apply filters
  const filteredChantiers = useMemo(() => {
    if (!chantiersList.length) return [];
    if (!chantierFilters.length) return chantiersList;
    return chantiersList.filter((c) => chantierFilters.includes(c));
  }, [chantiersList, chantierFilters]);

  const isOuvrierAllowed = (o) => {
    if (!ouvrierFilters.length) return true;
    return ouvrierFilters.includes(o);
  };

  // Global summary
  const globalSummary = useMemo(() => {
    let totalJours = 0;
    let totalHeures = 0;
    let chantierCount = 0;
    let ouvrierSet = new Set();

    filteredChantiers.forEach((chantier) => {
      const ouvriers = pivot[chantier] || {};
      chantierCount += 1;

      Object.entries(ouvriers).forEach(([ouvrier, joursHeures]) => {
        if (!isOuvrierAllowed(ouvrier)) return;

        ouvrierSet.add(ouvrier);
        const cells = dateKeys.map((k) => joursHeures[k] || { jours: 0, heures: 0 });
        totalJours += sum(cells, 'jours');
        totalHeures += sum(cells, 'heures');
      });
    });

    return {
      chantierCount,
      ouvrierCount: ouvrierSet.size,
      totalJours,
      totalHeures,
    };
  }, [filteredChantiers, pivot, dateKeys, ouvrierFilters]); // eslint-disable-line react-hooks/exhaustive-deps

  // =========================
  // 🎨 react-select common
  // =========================
  const selectClassName = 'resume-react-select';
  const selectPropsCommon = {
    isSearchable: true,
    isClearable: true,
    filterOption,
    className: selectClassName,
    classNamePrefix: 'react-select',
    placeholder: 'Rechercher...',
    menuPortalTarget: document.body,
    styles: {
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };

  // =========================
  // 📗 Export Excel (Pivot actuel)
  // =========================
  const exportToExcel = () => {
    try {
      if (!pivot || !Object.keys(pivot).length || !dateKeys.length) {
        setError('📭 Rien à exporter.');
        return;
      }

      // Helpers for Excel values
      const valExcel = (n) => {
        const num = Number(n || 0);
        if (hideZeros && num === 0) return ''; // cellules vides si hideZeros
        return num; // number -> Excel number
      };

      // 1) Build header rows (2 rows like pivot)
      const topHeader = ['Chantier', 'Ouvrier'];
      const subHeader = ['', ''];

      dateKeys.forEach((k) => {
        topHeader.push(dateLabelsByKey[k] || k, ''); // merge visually in excel? (we won't merge, but keep structure)
        subHeader.push('Jours', 'Heures');
      });

      topHeader.push('Totaux', '');
      subHeader.push('Jours', 'Heures');

      // 2) Build data rows with filters
      const rows = [];
      filteredChantiers.forEach((chantier) => {
        const ouvriersObj = pivot[chantier] || {};
        const ouvriersEntries = Object.entries(ouvriersObj)
          .filter(([o]) => isOuvrierAllowed(o))
          .sort((a, b) => a[0].localeCompare(b[0]));

        if (!ouvriersEntries.length) return;

        // Totaux chantier
        const chantierTotalsByDate = {};
        dateKeys.forEach((k) => {
          chantierTotalsByDate[k] = { jours: 0, heures: 0 };
        });

        ouvriersEntries.forEach(([, joursHeures]) => {
          dateKeys.forEach((k) => {
            const v = joursHeures[k] || { jours: 0, heures: 0 };
            chantierTotalsByDate[k].jours += Number(v.jours || 0);
            chantierTotalsByDate[k].heures += Number(v.heures || 0);
          });
        });

        const chantierTotalCells = dateKeys.map((k) => chantierTotalsByDate[k]);
        const chantierTotalJours = sum(chantierTotalCells, 'jours');
        const chantierTotalHeures = sum(chantierTotalCells, 'heures');

        // Ouvriers rows
        ouvriersEntries.forEach(([ouvrier, joursHeures]) => {
          const cells = dateKeys.map((k) => joursHeures[k] || { jours: 0, heures: 0 });
          const totalJours = sum(cells, 'jours');
          const totalHeures = sum(cells, 'heures');

          const row = [chantier, ouvrier];
          cells.forEach((v) => {
            row.push(valExcel(v.jours), valExcel(v.heures));
          });
          row.push(totalJours, totalHeures);

          rows.push(row);
        });

        // TOTAL CHANTIER row (option)
        if (showChantierTotals) {
          const totalRow = [chantier, '🧮 TOTAL CHANTIER'];
          chantierTotalCells.forEach((v) => {
            totalRow.push(valExcel(v.jours), valExcel(v.heures));
          });
          totalRow.push(chantierTotalJours, chantierTotalHeures);
          rows.push(totalRow);
        }
      });

      // 3) Add meta rows on top
      const meta = [
        ['Résumé Pointage'],
        [`Période : ${start} → ${end}`],
        [`Export le : ${format(new Date(), 'dd/MM/yyyy HH:mm')}`],
        [],
      ];

      const aoa = [...meta, topHeader, subHeader, ...rows];

      // 4) Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(aoa);

      // 5) Column widths
      const cols = [];
      cols.push({ wch: 28 }); // Chantier
      cols.push({ wch: 34 }); // Ouvrier
      // dates cols (jours/heures)
      dateKeys.forEach(() => {
        cols.push({ wch: 8 }, { wch: 8 });
      });
      // Totaux
      cols.push({ wch: 10 }, { wch: 10 });
      ws['!cols'] = cols;

      // 6) Add sheet and save
      XLSX.utils.book_append_sheet(wb, ws, 'ResumePointage');

      const fileName = `ResumePointage_${start}_au_${end}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
      setError("❌ Erreur lors de l'export Excel.");
    }
  };

  return (
    <div className="container mt-4 mb-5 resume-page">
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
        <h3 className="m-0">📊 Résumé Pointage</h3>

        <div className="d-flex align-items-center gap-2 flex-wrap">
          <Button
            variant="outline-secondary"
            className="resume-btn-clear"
            onClick={clearFilters}
            title="Effacer filtres"
          >
            <FaBroom className="me-2" />
            Effacer filtres
          </Button>

          <Button
            variant="success"
            onClick={exportToExcel}
            disabled={loading || !data || data.length === 0 || !dateKeys.length}
            title="Exporter Excel"
          >
            <FaFileExcel className="me-2" />
            Export Excel
          </Button>

          <Button
            variant="info"
            onClick={fetchData}
            disabled={!isValidRange || loading}
            className="resume-btn-generate"
          >
            {loading ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Chargement...
              </>
            ) : (
              <>
                <FaSearch className="me-2" />
                Générer
              </>
            )}
          </Button>
        </div>
      </div>

      {/* 🔔 Error */}
      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {/* 🎯 Controls */}
      <div className="row mt-3 mb-3 g-3">
        <div className="col-md-3">
          <Form.Label>
            <FaCalendarAlt className="me-2" />
            Date de début
          </Form.Label>
          <Form.Control type="date" value={start} onChange={(e) => setStart(e.target.value)} />
        </div>

        <div className="col-md-3">
          <Form.Label>
            <FaCalendarAlt className="me-2" />
            Date de fin
          </Form.Label>
          <Form.Control type="date" value={end} onChange={(e) => setEnd(e.target.value)} />
        </div>

        <div className="col-md-3">
          <Form.Label>
            <FaFilter className="me-2" />
            Filtrer par chantier (multi)
          </Form.Label>
          <Select
            {...selectPropsCommon}
            isMulti
            closeMenuOnSelect={false}
            options={chantierOptions}
            value={chantierValue}
            onChange={handleChantierChange}
            placeholder="Rechercher chantier..."
          />
          <div className="small text-muted mt-1">
            {chantierFilters.length ? `✅ ${chantierFilters.length} sélectionné(s)` : 'Tous'}
          </div>
        </div>

        <div className="col-md-3">
          <Form.Label>
            <FaFilter className="me-2" />
            Filtrer par ouvrier (multi)
          </Form.Label>
          <Select
            {...selectPropsCommon}
            isMulti
            closeMenuOnSelect={false}
            options={ouvrierOptions}
            value={ouvrierValue}
            onChange={handleOuvrierChange}
            placeholder="Rechercher ouvrier..."
          />
          <div className="small text-muted mt-1">
            {ouvrierFilters.length ? `✅ ${ouvrierFilters.length} sélectionné(s)` : 'Tous'}
          </div>
        </div>
      </div>

      {/* ⚙️ Options + Summary */}
      <div className="resume-toolbar d-flex align-items-center justify-content-between flex-wrap gap-3 mb-3">
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <Form.Check
            type="switch"
            id="hideZeros"
            label="🧼 Masquer les zéros"
            checked={hideZeros}
            onChange={(e) => setHideZeros(e.target.checked)}
          />
          <Form.Check
            type="switch"
            id="showChantierTotals"
            label="🧮 Afficher totaux chantier"
            checked={showChantierTotals}
            onChange={(e) => setShowChantierTotals(e.target.checked)}
          />
        </div>

        <div className="d-flex align-items-center gap-2 flex-wrap resume-badges">
          <Badge bg="secondary" className="resume-badge">
            🏗️ Chantiers : {globalSummary.chantierCount}
          </Badge>
          <Badge bg="secondary" className="resume-badge">
            👷 Ouvriers : {globalSummary.ouvrierCount}
          </Badge>
          <Badge bg="secondary" className="resume-badge">
            📅 Total Jours : {globalSummary.totalJours}
          </Badge>
          <Badge bg="secondary" className="resume-badge">
            ⏱️ Total Heures : {globalSummary.totalHeures}
          </Badge>
        </div>
      </div>

      {/* 📭 Empty state */}
      {!loading && !error && (!data || data.length === 0) && (
        <div className="resume-empty">📭 Aucun pointage trouvé pour cette période.</div>
      )}

      {/* 📊 Pivot */}
      {Object.keys(pivot).length > 0 && dateKeys.length > 0 && (
        <div className="resume-table-container">
          <div className="table-responsive resume-table-scroll">
            <table className="table table-bordered table-striped resume-pivot-table">
              <thead className="table-light">
                <tr>
                  <th rowSpan="2" className="sticky-col left-0 sticky-head">
                    Chantier
                  </th>
                  <th rowSpan="2" className="sticky-col left-240 sticky-head">
                    Ouvrier
                  </th>

                  {dateKeys.map((k) => (
                    <th key={k} colSpan="2" className="text-center sticky-head">
                      {dateLabelsByKey[k] || k}
                    </th>
                  ))}

                  <th colSpan="2" className="text-center sticky-head sticky-right-160">
                    Totaux
                  </th>
                </tr>

                <tr>
                  {dateKeys.map((k) => (
                    <React.Fragment key={k}>
                      <th className="text-center sticky-head-2">Jours</th>
                      <th className="text-center sticky-head-2">Heures</th>
                    </React.Fragment>
                  ))}
                  <th className="sticky-head-2 sticky-right-160">Jours</th>
                  <th className="sticky-head-2 sticky-right-0">Heures</th>
                </tr>
              </thead>

              <tbody>
                {filteredChantiers.map((chantier) => {
                  const ouvriersObj = pivot[chantier] || {};
                  const ouvriersEntries = Object.entries(ouvriersObj).filter(([o]) =>
                    isOuvrierAllowed(o)
                  );

                  if (!ouvriersEntries.length) return null;

                  // totals chantier
                  const chantierTotalsByDate = {};
                  dateKeys.forEach((k) => {
                    chantierTotalsByDate[k] = { jours: 0, heures: 0 };
                  });

                  ouvriersEntries.forEach(([, joursHeures]) => {
                    dateKeys.forEach((k) => {
                      const v = joursHeures[k] || { jours: 0, heures: 0 };
                      chantierTotalsByDate[k].jours += Number(v.jours || 0);
                      chantierTotalsByDate[k].heures += Number(v.heures || 0);
                    });
                  });

                  const chantierTotalCells = dateKeys.map((k) => chantierTotalsByDate[k]);
                  const chantierTotalJours = sum(chantierTotalCells, 'jours');
                  const chantierTotalHeures = sum(chantierTotalCells, 'heures');

                  return (
                    <React.Fragment key={chantier}>
                      {ouvriersEntries.map(([ouvrier, joursHeures], i) => {
                        const cells = dateKeys.map((k) => joursHeures[k] || { jours: 0, heures: 0 });
                        const totalJours = sum(cells, 'jours');
                        const totalHeures = sum(cells, 'heures');

                        return (
                          <tr key={`${chantier}-${ouvrier}`}>
                            {i === 0 && (
                              <td
                                rowSpan={ouvriersEntries.length + (showChantierTotals ? 1 : 0)}
                                className="sticky-col left-0 bg-white resume-sticky-shadow"
                              >
                                <div className="resume-chantier-cell">
                                  <span className="resume-chantier-title">{chantier}</span>
                                  {showChantierTotals && (
                                    <div className="resume-chantier-sub">
                                      🧮 {chantierTotalJours} j / {chantierTotalHeures} h
                                    </div>
                                  )}
                                </div>
                              </td>
                            )}

                            <td className="sticky-col left-240 bg-white resume-sticky-shadow-2">
                              {ouvrier}
                            </td>

                            {cells.map((val, idx) => {
                              const j = Number(val?.jours || 0);
                              const h = Number(val?.heures || 0);

                              const displayJ = hideZeros ? (j === 0 ? '' : j) : j;
                              const displayH = hideZeros ? (h === 0 ? '' : h) : h;

                              const cellClass =
                                j === 0 && h === 0 ? 'resume-cell-zero' : 'resume-cell-nonzero';

                              return (
                                <React.Fragment key={idx}>
                                  <td className={cellClass}>{displayJ}</td>
                                  <td className={cellClass}>{displayH}</td>
                                </React.Fragment>
                              );
                            })}

                            <td className="resume-total-cell sticky-right-160">
                              <strong>{totalJours}</strong>
                            </td>
                            <td className="resume-total-cell sticky-right-0">
                              <strong>{totalHeures}</strong>
                            </td>
                          </tr>
                        );
                      })}

                      {showChantierTotals && (
                        <tr className="resume-chantier-total-row">
                          <td className="sticky-col left-240 bg-white resume-sticky-shadow-2">
                            <strong>🧮 TOTAL CHANTIER</strong>
                          </td>

                          {chantierTotalCells.map((val, idx) => {
                            const j = Number(val?.jours || 0);
                            const h = Number(val?.heures || 0);

                            const displayJ = hideZeros ? (j === 0 ? '' : j) : j;
                            const displayH = hideZeros ? (h === 0 ? '' : h) : h;

                            return (
                              <React.Fragment key={idx}>
                                <td className="resume-chantier-total-cell">
                                  <strong>{displayJ}</strong>
                                </td>
                                <td className="resume-chantier-total-cell">
                                  <strong>{displayH}</strong>
                                </td>
                              </React.Fragment>
                            );
                          })}

                          <td className="resume-chantier-total-cell sticky-right-160">
                            <strong>{chantierTotalJours}</strong>
                          </td>
                          <td className="resume-chantier-total-cell sticky-right-0">
                            <strong>{chantierTotalHeures}</strong>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResumePointagePage;
